import { useState } from "react";
import {
  isValidLotsText,
  lotDecimals,
  lotsTextToQuantity,
  quantityAfterLotsStep,
  quantityToLotsText,
} from "../../../services/utilities/lotSize";

// Lots input kept in sync with a whole-number quantity. Typed lots text stays visible until
// quantity changes some other way; otherwise lots are derived from quantity.
// onQuantityChange receives a whole number, or "" when Lots is cleared.
export const useLotsField = ({ quantity, lotSize, onQuantityChange, resetKey }) => {
  const [typed, setTyped] = useState(null); // { text, quantity, lotSize, resetKey }
  const quantityKey = quantity === undefined || quantity === null ? "" : String(quantity);
  const decimals = lotSize ? lotDecimals(lotSize) : 0;

  const typedStillApplies =
    typed !== null &&
    typed.quantity === quantityKey &&
    typed.lotSize === lotSize &&
    typed.resetKey === resetKey;

  // Clear stale typed text (not just hide it), so it can't reappear if quantity returns to that value.
  if (typed !== null && !typedStillApplies) {
    setTyped(null);
  }

  let value = "";
  if (lotSize) {
    value = typedStillApplies ? typed.text : quantityToLotsText(quantity, lotSize);
  }

  const handleChange = (rawText) => {
    if (!lotSize) return;
    // "007" -> "7", "00.5" -> "0.5"; a single "0" stays so "0.5" can be typed.
    const text = String(rawText).replace(/^0+(?=\d)/, "");
    if (!isValidLotsText(text, decimals)) return;

    if (text === "") {
      setTyped(null);
      onQuantityChange("");
      return;
    }

    const newQuantity = lotsTextToQuantity(text, lotSize);
    if (newQuantity === null) return; // too large to represent exactly

    setTyped({ text, quantity: String(newQuantity), lotSize, resetKey });
    onQuantityChange(newQuantity);
  };

  const handleStep = (direction) => {
    if (!lotSize) return;
    setTyped(null);
    onQuantityChange(quantityAfterLotsStep(value, lotSize, direction));
  };

  return { value, decimals, handleChange, handleStep };
};
